/**
 * Push Diagnostics Page (Card 17) — admin only.
 *
 * Exists because push is the most silent subsystem in the app: a wrong key, an expired
 * token, a missing config file and a denied permission all present identically as "no
 * notification arrived". Three separate silent failures of exactly that kind were found on
 * this project in a single day.
 *
 * The screen's one job is to never say "no notifications". It resolves exactly one state
 * from the Card 17 taxonomy and states the cause in words — most importantly
 * `native_push_unavailable`, which is what a build missing `google-services.json` /
 * `GoogleService-Info.plist` / the plugin itself must render as, instead of looking like an
 * idle-but-healthy app. See GOOGLE_OAUTH_NATIVE_MOBILE_PLAN.md §13.6.
 */

import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertTriangle,
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Info,
  RefreshCw,
  Send,
  XCircle
} from 'lucide-react';
import { usePushDiagnostics } from '@/hooks/usePushDiagnostics';
import type { PushState } from '@/types/pushDiagnostics.types';

/** Visual weight per state. `native_push_unavailable` is the loudest on purpose. */
const STATE_STYLES: Record<PushState, { label: string; className: string; Icon: typeof Info }> = {
  native_push_unavailable: {
    label: 'Build misconfigured',
    className: 'bg-red-50 border-red-300 text-red-900',
    Icon: XCircle
  },
  permission_denied: {
    label: 'Permission denied',
    className: 'bg-amber-50 border-amber-300 text-amber-900',
    Icon: AlertTriangle
  },
  no_token_yet: {
    label: 'No token yet',
    className: 'bg-slate-50 border-slate-300 text-slate-900',
    Icon: Info
  },
  token_stale: {
    label: 'Token stale',
    className: 'bg-amber-50 border-amber-300 text-amber-900',
    Icon: AlertTriangle
  },
  delivered: {
    label: 'Registered',
    className: 'bg-green-50 border-green-300 text-green-900',
    Icon: CheckCircle2
  }
};

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return '—';
  return new Date(parsed).toLocaleString();
}

/** A label/value row. `value` is rendered verbatim — never massaged into a friendlier lie. */
function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600 shrink-0">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right break-all">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Tri-state renderer. `null` means "not determined yet", which is NOT the same as `false`
 * and must not be drawn as one — reporting "Firebase: missing" on a build that simply has
 * not tried to register yet is a false alarm, and false alarms are how real ones get ignored.
 */
function TriState({ value, trueText, falseText }: { value: boolean | null; trueText: string; falseText: string }) {
  if (value === null) return <span className="text-gray-500">Not determined yet</span>;
  return value
    ? <span className="text-green-700">{trueText}</span>
    : <span className="text-red-700">{falseText}</span>;
}

export default function PushDiagnosticsPage() {
  const navigate = useNavigate();
  const { loading, resolved, input, server, serverError, refresh, sendTest, testing, lastTest } =
    usePushDiagnostics();

  const style = resolved ? STATE_STYLES[resolved.state] : null;
  const StateIcon = style?.Icon ?? Info;

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Go back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <BellRing className="h-5 w-5" /> Push Diagnostics
            </h1>
            <p className="text-sm text-gray-600">Why notifications are or are not arriving on this device.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading && !resolved ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : null}

        {/* ── The verdict. One state, stated in words. ───────────────────── */}
        {resolved && style ? (
          <div className={`rounded-lg border p-4 ${style.className}`}>
            <div className="flex items-start gap-3">
              <StateIcon className="h-5 w-5 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{style.label}</p>
                <p className="text-sm font-medium">{resolved.headline}</p>
                {resolved.remedy ? <p className="text-sm opacity-90">{resolved.remedy}</p> : null}
                {resolved.detail ? (
                  // Verbatim, unfriendly, and searchable — this is the string that identifies
                  // which of the failure modes actually happened.
                  <pre className="mt-2 text-xs whitespace-pre-wrap opacity-80 bg-white/60 rounded p-2">
                    {resolved.detail}
                  </pre>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Self-test ─────────────────────────────────────────────────── */}
        <Section title="Self-test">
          <p className="text-sm text-gray-600 mb-3">
            Sends a real notification to every device registered to this account and reports what
            each one did.
          </p>
          <Button onClick={() => void sendTest()} disabled={testing} size="sm">
            <Send className="h-4 w-4 mr-2" />
            {testing ? 'Sending…' : 'Send test notification'}
          </Button>

          {lastTest ? (
            <div
              className={`mt-3 rounded-md border p-3 text-sm ${
                lastTest.ok ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'
              }`}
            >
              <p>{lastTest.message}</p>
              {lastTest.outcomes.length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs">
                  {lastTest.outcomes.map((outcome, index) => (
                    <li key={index}>
                      <span className="font-medium">{outcome.platform}</span>
                      {outcome.deviceName ? ` · ${outcome.deviceName}` : ''} → {outcome.outcome}
                      {outcome.errorCode ? ` (${outcome.errorCode})` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </Section>

        {/* ── This device ───────────────────────────────────────────────── */}
        {input ? (
          <Section title="This device">
            <Row label="Platform" value={input.platform} />
            <Row label="OS permission" value={input.permission} />
            <Row
              label="Push plugin in binary"
              value={
                input.isNative ? (
                  <TriState
                    value={input.prerequisites.pluginRegistered}
                    trueText="Registered"
                    falseText="MISSING — push cannot work"
                  />
                ) : (
                  <span className="text-gray-500">n/a (web build)</span>
                )
              }
            />
            <Row
              label="Firebase config in build"
              value={
                input.isNative ? (
                  <TriState
                    value={input.prerequisites.firebaseConfigured}
                    trueText="Present (a token was issued)"
                    falseText="MISSING — google-services.json / GoogleService-Info.plist"
                  />
                ) : (
                  <span className="text-gray-500">n/a (web build)</span>
                )
              }
            />
            <Row label="Token on device" value={input.localToken.present ? 'Yes' : 'No'} />
            <Row label="Token suffix" value={input.localToken.suffix ?? '—'} />
            <Row
              label="Token synced to server"
              value={input.localToken.pendingSync ? <span className="text-amber-700">Pending — last sync failed</span> : 'Yes'}
            />
            <Row
              label="Last notification seen here"
              value={
                input.lastDelivery
                  ? `${formatTimestamp(input.lastDelivery.receivedAt)} (${input.lastDelivery.via})`
                  : 'None yet'
              }
            />
          </Section>
        ) : null}

        {/* ── Server ────────────────────────────────────────────────────── */}
        {serverError ? (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-medium">Server-side diagnostics unavailable</p>
            <p>{serverError}</p>
          </div>
        ) : null}

        {server ? (
          <>
            <Section title="Server configuration">
              <Row
                label="Firebase credentials (native push)"
                value={
                  server.server.fcmConfigured
                    ? <span className="text-green-700">Configured</span>
                    : <span className="text-red-700">NOT configured — no native push can be sent</span>
                }
              />
              <Row
                label="VAPID keys (web push)"
                value={
                  server.server.webPushConfigured
                    ? <span className="text-green-700">Configured</span>
                    : <span className="text-red-700">NOT configured — no web push can be sent</span>
                }
              />
            </Section>

            <Section title="Registered devices">
              {server.subscriptions.length === 0 ? (
                <p className="text-sm text-gray-600">
                  The server holds no active push registration for this account.
                </p>
              ) : (
                server.subscriptions.map((sub) => (
                  <div key={sub.id} className="py-2 border-b border-gray-100 last:border-0">
                    <p className="text-sm font-medium text-gray-900">
                      {sub.platform}
                      {sub.deviceName ? ` · ${sub.deviceName}` : ''}
                    </p>
                    <p className="text-xs text-gray-600">
                      registered {formatTimestamp(sub.registeredAt)} · last synced{' '}
                      {formatTimestamp(sub.lastSyncedAt)}
                      {sub.tokenSuffix ? ` · …${sub.tokenSuffix}` : ''}
                    </p>
                  </div>
                ))
              )}
            </Section>

            <Section title="Delivery history (last 30 days)">
              {!server.delivery.available ? (
                // NOT the same as "nothing was sent" — say which one it is.
                <p className="text-sm text-amber-800">
                  Delivery history could not be read. The Card 17 migration may not be applied in
                  this environment.
                </p>
              ) : server.delivery.summaries.length === 0 ? (
                <p className="text-sm text-gray-600">No push has been attempted for this account yet.</p>
              ) : (
                server.delivery.summaries.map((summary) => (
                  <div key={summary.platform} className="py-2 border-b border-gray-100 last:border-0">
                    <p className="text-sm font-medium text-gray-900">
                      {summary.platform} — {summary.total} attempt(s)
                    </p>
                    <p className="text-xs text-gray-600">
                      {Object.entries(summary.outcomes)
                        .map(([outcome, count]) => `${outcome}: ${count}`)
                        .join(' · ')}
                    </p>
                    <p className="text-xs text-gray-600">
                      last success {formatTimestamp(summary.lastSuccessAt)} · last failure{' '}
                      {formatTimestamp(summary.lastFailureAt)}
                      {summary.lastErrorCode ? ` (${summary.lastErrorCode})` : ''}
                    </p>
                  </div>
                ))
              )}
            </Section>

            {server.delivery.recentAttempts.length > 0 ? (
              <Section title="Recent attempts">
                <ul className="space-y-1 text-xs text-gray-700">
                  {server.delivery.recentAttempts.map((attempt, index) => (
                    <li key={index} className="flex justify-between gap-3">
                      <span className="shrink-0">{formatTimestamp(attempt.createdAt)}</span>
                      <span className="text-right break-all">
                        {attempt.platform} · {attempt.context} · {attempt.outcome}
                        {attempt.errorCode ? ` (${attempt.errorCode})` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}
          </>
        ) : null}
      </div>
    </AppLayout>
  );
}
